import { useAuth } from './useAuth';

/**
 * Catalog of clinic-mgmt verb-level permission names.
 * These mirror the dbo.ItemPropertyTypes Mnemonic seed and the
 * [Authorize(Roles = "SysAdmin,UserCanXxx")] strings on each API action.
 *
 * SecurityDbService flattens granted ItemPropertyTypes permissions into the
 * JWT `Roles[]` claim alongside named roles like 'SysAdmin', so the UI
 * checks them via the same roles array.
 */
export const ClinicMgmtPermissions = {
  // Clinic profile (CRUD)
  ListClinicProfiles: 'UserCanListClinicProfiles',
  ViewClinicProfile: 'UserCanViewClinicProfile',
  CreateClinicProfile: 'UserCanCreateClinicProfile',
  EditClinicProfile: 'UserCanEditClinicProfile',
  DeleteClinicProfile: 'UserCanDeleteClinicProfile',
  // Profile sub-features
  EditClinicCapabilities: 'UserCanEditClinicCapabilities',
  EditClinicCategories: 'UserCanEditClinicCategories',
  EditClinicServices: 'UserCanEditClinicServices',
  EditClinicContacts: 'UserCanEditClinicContacts',
  EditClinicAddress: 'UserCanEditClinicAddress',
  EditClinicOperatingHours: 'UserCanEditClinicOperatingHours',
  CreateClinicNote: 'UserCanCreateClinicNote',
  EditClinicNote: 'UserCanEditClinicNote',
  DeleteClinicNote: 'UserCanDeleteClinicNote',
  // Verification
  ListClinicVerificationQueue: 'UserCanListClinicVerificationQueue',
  ReviewClinicVerification: 'UserCanReviewClinicVerification',
  CompleteClinicVerification: 'UserCanCompleteClinicVerification',
  // JobSites — customer locations flagged as transient sites.
  // Verb registered by SLV2-2472 (JobSite cluster, epic SLV2-2470, SourceLink side);
  // consumed here by the Clinic Network JobSites page (SLV2-2772).
  CreateJobSite: 'UserCanCreateJobSite',
  // Relationships / Coverage
  ListClinicRelationships: 'UserCanListClinicRelationships',
  ViewClinicRelationship: 'UserCanViewClinicRelationship',
  CreateClinicRelationship: 'UserCanCreateClinicRelationship',
  EditClinicRelationship: 'UserCanEditClinicRelationship',
  DeleteClinicRelationship: 'UserCanDeleteClinicRelationship',
  // Hierarchy
  ListClinicHierarchy: 'UserCanListClinicHierarchy',
  EditClinicHierarchy: 'UserCanEditClinicHierarchy',
  // Tasks
  ListClinicTaskRequests: 'UserCanListClinicTaskRequests',
  ViewClinicTaskRequest: 'UserCanViewClinicTaskRequest',
  CreateClinicTaskRequest: 'UserCanCreateClinicTaskRequest',
  EditClinicTaskRequest: 'UserCanEditClinicTaskRequest',
  AssignClinicTaskRequest: 'UserCanAssignClinicTaskRequest',
  CompleteClinicTaskRequest: 'UserCanCompleteClinicTaskRequest',
  RejectClinicTaskRequest: 'UserCanRejectClinicTaskRequest',
  // Procedures
  ListClinicProcedures: 'UserCanListClinicProcedures',
  CreateClinicProcedure: 'UserCanCreateClinicProcedure',
  MapClinicProcedureToGroup: 'UserCanMapClinicProcedureToGroup',
  // Cutover / admin tools
  RunClinicMatrixImport: 'UserCanRunClinicMatrixImport',
  RunClinicGeocoding: 'UserCanRunClinicGeocoding',
  RunClinicDeactivation: 'UserCanRunClinicDeactivation',
  RunClinicAutoHierarchy: 'UserCanRunClinicAutoHierarchy',
  RunLabCorpImport: 'UserCanRunLabCorpImport',
  RunClinicBulkImport: 'UserCanRunClinicBulkImport',
  ManageClinicProcedureTemplates: 'UserCanManageClinicProcedureTemplates',
  // Lookup admin
  ManageClinicCapabilityTypes: 'UserCanManageClinicCapabilityTypes',
  ManageClinicCategoryTypes: 'UserCanManageClinicCategoryTypes',
  // Data quality + settings
  ViewClinicDataQuality: 'UserCanViewClinicDataQuality',
  ViewClinicMgmtSettings: 'UserCanViewClinicMgmtSettings',
  EditClinicMgmtSettings: 'UserCanEditClinicMgmtSettings',
} as const;

export type ClinicMgmtPermission =
  (typeof ClinicMgmtPermissions)[keyof typeof ClinicMgmtPermissions];

const SYS_ADMIN_ROLE = 'SysAdmin';

export interface PermissionsApi {
  /** Raw roles array from JWT — includes role names AND granted permission strings. */
  roles: string[];
  /** True if the user has the SysAdmin role (always passes permission checks). */
  isSysAdmin: boolean;
  /** Returns true if the user is SysAdmin or has the named permission. */
  hasPermission: (permission: ClinicMgmtPermission) => boolean;
  /** Returns true if the user is SysAdmin or has at least one of the named permissions. */
  hasAnyPermission: (...permissions: ClinicMgmtPermission[]) => boolean;
  /** Returns true if the user is SysAdmin or has ALL of the named permissions. */
  hasAllPermissions: (...permissions: ClinicMgmtPermission[]) => boolean;
}

/**
 * Hook for checking the current user's verb-level permissions.
 *
 * SysAdmin is treated as an always-allow — matching the API-side
 * `[Authorize(Roles = "SysAdmin,UserCanXxx")]` convention.
 */
export function usePermissions(): PermissionsApi {
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  const isSysAdmin = roles.includes(SYS_ADMIN_ROLE);

  const hasPermission = (permission: ClinicMgmtPermission): boolean =>
    isSysAdmin || roles.includes(permission);

  const hasAnyPermission = (...permissions: ClinicMgmtPermission[]): boolean => {
    if (isSysAdmin) return true;
    return permissions.some((p) => roles.includes(p));
  };

  const hasAllPermissions = (...permissions: ClinicMgmtPermission[]): boolean => {
    if (isSysAdmin) return true;
    return permissions.every((p) => roles.includes(p));
  };

  return { roles, isSysAdmin, hasPermission, hasAnyPermission, hasAllPermissions };
}
